"use client";

import { c as _c } from "react/compiler-runtime";
import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { getInsuranceDecisionCoverage } from "@/configuration/groups/tpr/workflows/insurance-decision-coverage";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import { useConfirmInsuranceNetworkMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { receivingAssignmentOutputSchema } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
export function TprNetworkStatus(t0) {
  const $ = _c(34);
  const {
    task,
    steps,
    coverage,
    requirements
  } = t0;
  const mutation = useConfirmInsuranceNetworkMutation();
  const invalidate = useInvalidateServerComponent("getReferralInstructions");
  let t1;
  if ($[0] !== steps) {
    const assignmentStep = steps.find(_temp);
    t1 = receivingAssignmentOutputSchema.safeParse(assignmentStep?.output);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsed = t1;
  const assignment = parsed.success ? parsed.data : null;
  let t2;
  if ($[2] !== coverage) {
    t2 = getInsuranceDecisionCoverage(coverage);
    $[2] = coverage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const policy = t2;
  if (policy.selfPay) {
    return null;
  }
  if (requirements.clinicNetwork === "in_network" && requirements.providerNetwork === "in_network") {
    const t3 = requirements.networkVerification ? "In-network \u2014 confirmed by Ops" : "In-network \u2014 confirmed by insurance grid";
    let t4;
    if ($[4] !== t3) {
      t4 = <p className="font-medium">{t3}</p>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="text-muted-foreground">Both the clinic and receiving provider accept this plan. No additional network confirmation is needed. Coverage eligibility is checked separately.</p>;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t4) {
      t6 = <div className="space-y-1 rounded-md border bg-muted/50 p-3 text-sm">{t4}{t5}</div>;
      $[7] = t4;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  if (!task.patient?.id || !assignment?.providerNpi || !assignment.targetDepartmentId || assignment.scope === "manual_review" || task.status === TaskStatus.PROCESSING) {
    return null;
  }
  const variantKey = requirements.networkVariantKey;
  if (variantKey === null || variantKey === undefined) {
    let t3;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className="text-sm">Identify the insurance plan variant before confirming in-network status.</p>;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t3;
  if ($[10] !== assignment.providerNpi || $[11] !== assignment.targetDepartmentId || $[12] !== policy.insuranceId || $[13] !== policy.insurancePackageId || $[14] !== task.patient.id || $[15] !== variantKey) {
    t3 = {
      expectedVariantKey: variantKey,
      patientId: task.patient.id,
      insuranceId: policy.insuranceId,
      packageNumber: policy.insurancePackageId,
      providerNpi: assignment.providerNpi,
      departmentId: assignment.targetDepartmentId
    };
    $[10] = assignment.providerNpi;
    $[11] = assignment.targetDepartmentId;
    $[12] = policy.insuranceId;
    $[13] = policy.insurancePackageId;
    $[14] = task.patient.id;
    $[15] = variantKey;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const scope = t3;
  let t4;
  if ($[17] !== invalidate || $[18] !== mutation || $[19] !== scope || $[20] !== task.displayId || $[21] !== task.id) {
    t4 = async () => {
      ;
      try {
        await mutation.mutateAsync({
          taskId: task.id,
          displayId: task.displayId,
          scope
        });
        refreshTprWorkflowProgress(invalidate);
        toast({
          title: "In-network status confirmed"
        });
      } catch (t5) {
        const error = t5;
        toast({
          title: "Unable to save network confirmation",
          description: error instanceof Error ? error.message : "Refresh and try again.",
          variant: "destructive"
        });
      }
    };
    $[17] = invalidate;
    $[18] = mutation;
    $[19] = scope;
    $[20] = task.displayId;
    $[21] = task.id;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const confirm = t4;
  let t5;
  if ($[23] !== assignment.providerFamilyName || $[24] !== assignment.providerGivenName || $[25] !== policy.insurancePackageId) {
    t5 = <p className="text-sm">After verifying that the clinic and {assignment.providerGivenName} {assignment.providerFamilyName} accept this patient's plan (package {policy.insurancePackageId}), confirm below. This also applies to future documents and referrals for this patient while the insurance policy, provider, and clinic stay the same.</p>;
    $[23] = assignment.providerFamilyName;
    $[24] = assignment.providerGivenName;
    $[25] = policy.insurancePackageId;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  const t6 = mutation.isPending ? "Saving..." : "Confirm in-network";
  let t7;
  if ($[27] !== confirm || $[28] !== mutation.isPending || $[29] !== t6) {
    t7 = <Button size="sm" onClick={confirm} disabled={mutation.isPending}>{t6}</Button>;
    $[27] = confirm;
    $[28] = mutation.isPending;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== t5 || $[32] !== t7) {
    t8 = <div className="space-y-2">{t5}{t7}</div>;
    $[31] = t5;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}
function _temp(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT && step.status === TaskStepStatus.COMPLETED;
}